/**
 * Date automations the skill's own words point at (a skill about RFE
 * responses could start itself as the deadline nears), as rows of the
 * Triggers card while it has none: the sentence, why, and Add. Adding saves
 * the automation through the skills update; nothing starts until the skill
 * is enabled.
 */
import { Hourglass, Plus } from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

import { invalidateActiveFirmSkills } from "@/features/firmKnowledge/hooks/useActiveFirmSkills";
import type { FirmSkillsState } from "@/features/firmKnowledge/hooks/useFirmSkills";
import type {
  FirmSkill,
  SuggestedAutomation
} from "@/features/firmKnowledge/types";

type Props = {
  skill: FirmSkill;
  skills: Pick<FirmSkillsState, "update">;
};

export const SuggestedAutomations: FC<Props> = ({ skill, skills }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const suggestions = skill.suggested_automations ?? [];
  if (suggestions.length === 0) return null;

  const add = async (s: SuggestedAutomation) => {
    setBusy(s.action);
    try {
      await skills.update(skill.id, {
        triggers: [...skill.triggers, s.trigger],
        change_note: "Added a suggested automation"
      });
      invalidateActiveFirmSkills();
      toast.success("Automation added");
      if (skill.status !== "active") {
        toast.message("It starts runs once the automation is enabled.");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not add it.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {suggestions.map((s) => (
        <div
          key={s.action}
          className="flex items-start gap-2.5 px-3 py-2.5"
          data-testid="skill-suggested-automation"
        >
          <Hourglass className="mt-0.5 size-4 shrink-0 text-fg-3" />
          <span className="min-w-0 flex-1">
            <span className="block text-base text-fg">
              <span className="text-fg-3">Suggested: </span>
              {s.sentence}
            </span>
            <span className="block text-sm text-fg-3">{s.why}</span>
          </span>
          <Button
            size="xs"
            variant="secondary"
            disabled={busy !== null}
            onClick={() => void add(s)}
            data-testid={`skill-add-automation-${s.action}`}
          >
            <Plus /> Add
          </Button>
        </div>
      ))}
    </>
  );
};
